import React, { useState, useCallback, useEffect } from "react";

import { Constant } from "../../../helper/constant";
import { useNavigate } from "react-router-dom";
import ProductInvoiceTable from "../../product_invoice/component/productInvoiceTable";
import ModalDetailProductInvoice from "../../product_invoice/component/modalDetailProductInvoice";
import ModalDeleteConfirmation from "../../../components/modal/modalDeleteConfirmation";
import PaginationFooter from "../../../components/footer/paginationFooter";
import ExpiredProductInvoiceFilter from "../components/ExpiredProductInvoiceFilter";
import { formatDateTransaction } from "../../../helper/formatDate";

const ExpiredProductInvoice = () => {
  // =====================
  // Constant Definition
  // =====================

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isOpenModalDetailProductInvoice, setIsOpenModalDetailProductInvoice] =
    useState(false);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [selectedProductData, setSelectedProductData] = useState({
    product_invoice_id: "",
    product_invoice_supplier_id: "",
    product_invoice_document_number: "",
    product_invoice_date: "",
    product_invoice_due_date: "",
    product_invoice_products: [],
    product_invoice_total_discount: "",
    product_invoice_total_dpp: "",
    product_invoice_total_tax: "",
    product_invoice_total_bill: "",
  });
  const [listProductData, setListProductData] = useState(null);
  const [isOpenModalDeleteProduct, setIsOpenModalDeleteProduct] =
    useState(false);
  const [idProductToDelete, setIdProductToDelete] = useState("");

  // =====================
  // Function Definition
  // =====================

  const onDetailProduct = (productData) => {
    setSelectedProductData(productData);
    setIsOpenModalDetailProductInvoice(true);
  };

  const onPaymentInvoiceClicked = (invoiceData) => {
    updateInvoicePaymentStatus(invoiceData.product_invoice_id);
  };

  const onEditProduct = (productData) => {
    setSelectedProductData(productData);
    navigate(`/edit-produk-invoice`, {
      state: { productData },
    });
  };

  const onDeleteProduct = (productData) => {
    setIsOpenModalDeleteProduct(true);
    setIdProductToDelete(productData.product_invoice_id);
  };

  const onDeleteProductConfirmed = () => {
    setIsOpenModalDeleteProduct(false);
    deleteProduct();
  };

  const onCloseModalDeleteProduct = () => {
    setIdProductToDelete("");
    setIsOpenModalDeleteProduct(false);
  };

  const onCloseModal = () => {
    resetSelectedProductDataValue();
    setIsOpenModalDetailProductInvoice(false);
  };

  const resetSelectedProductDataValue = () => {
    setSelectedProductData({
      product_invoice_id: "",
      product_invoice_supplier_id: "",
      product_invoice_document_number: "",
      product_invoice_date: "",
      product_invoice_due_date: "",
      product_invoice_products: [],
      product_invoice_total_discount: "",
      product_invoice_total_dpp: "",
      product_invoice_total_tax: "",
      product_invoice_total_bill: "",
    });
  };

  const onNextPageClicked = () => {
    if (page < totalPage) {
      setPage((prevPage) => {
        return prevPage + 1;
      });
    }
  };

  const onPreviousPageClicked = () => {
    if (page !== 1) {
      setPage((prevPage) => {
        return prevPage - 1;
      });
    }
  };

  const onSubmitFilter = (expiredDate) => {
    getProductData(expiredDate)
  }

  // =====================
  // Request Method
  // =====================

  const getProductData = useCallback(async (expiredDate) => {
    try {
      setIsLoading(true);

      const params = new URLSearchParams({
        page: page,
      })

      if (expiredDate) {
        params.append('expired_at', formatDateTransaction(expiredDate, "daily"))
      }

      const response = await fetch(
        Constant.BASE_API_URL + "/invoice-produk/expired-produk?" + params.toString(),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        }
      );
      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
      const json = await response.json();
      const data = json.data;
      const product = data.product_invoice;
      if (!product) {
        setListProductData([]);
      }
      setListProductData(product);
      const totalPage = data.meta.total_page;
      setTotalPage(totalPage);
    } catch (error) {
      setListProductData([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, navigate]);

  const deleteProduct = async () => {
    try {
      setIsLoading(true);
      const productId = idProductToDelete;
      const response = await fetch(
        Constant.BASE_API_URL + "/invoice-produk/" + productId,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        }
      );
      if (!response.ok) {
        alert("Gagal Menghapus Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      setIdProductToDelete("");
      getProductData();
    }
  };

  const updateInvoicePaymentStatus = async (invoiceId) => {
    try {
      setIsLoading(true);
      const response = await fetch(
        Constant.BASE_API_URL +
        "/invoice-produk/update-invoice-status/" +
        invoiceId,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        }
      );
      if (!response.ok) {
        alert("Gagal Mengubah Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getProductData();
    }
  };

  // =====================
  // Use Effect Method
  // =====================

  useEffect(() => {
    getProductData();
  }, [page, getProductData]);

  return isLoading ? (
    <div className="loading-container">
      <div className="loader"></div>
      <p>Sedang Memuat Mohon Menunggu</p>
    </div>
  ) : (
    <div>
      <h1 className="mb-4">Produk Expired</h1>
      <ExpiredProductInvoiceFilter onSubmitFilter={onSubmitFilter} />
      <ProductInvoiceTable
        page={page}
        product={listProductData}
        onEditProductClicked={onEditProduct}
        onDeleteProductClicked={onDeleteProduct}
        onDetailProductClicked={onDetailProduct}
        onPaymentInvoiceClicked={onPaymentInvoiceClicked}
      />
      <ModalDetailProductInvoice
        isShowModal={isOpenModalDetailProductInvoice}
        onCloseModal={onCloseModal}
        productObject={selectedProductData.product_invoice_products}
        key={Date.now()}
      />
      <ModalDeleteConfirmation
        isShowModal={isOpenModalDeleteProduct}
        onCloseModal={onCloseModalDeleteProduct}
        onDeleteConfirmed={onDeleteProductConfirmed}
        idToDelete={idProductToDelete}
      />
      <PaginationFooter
        isPreviousActive={page > 1}
        isNextActive={page < totalPage}
        onNextClick={onNextPageClicked}
        onPrevClick={onPreviousPageClicked}
      />
    </div>
  );
};

export default ExpiredProductInvoice;
